import { CoherenceDataGrid, CoherenceDataGridColumn } from "@coherence-design-system/controls";
import { IMenuItemDb } from "@dining/api-client";
import { CheckboxVisibility, Selection, SelectionMode, Stack, StackItem, Text } from "@fluentui/react";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedItem } from "../../reducers/diningReducer";
import { currencySelector, selectedItemSelector } from "../../selectors/diningSelector";
import styles from "../../styles/content.module.css";
import { EditDeleteColumn } from "./EditDeleteColumn";

export const ItemList = (props: { itemBank: IMenuItemDb[] }) => {
  const dispatch = useDispatch();
  const currentCurrency = useSelector(currencySelector);
  const selectedItem = useSelector(selectedItemSelector);

  // The Selection object created below determines what is selected by the data grid.
  // Items in a Selection require a key, so we extend IMenuItemDb to have one...

  interface MenuItemDbWithKey extends IMenuItemDb {
    key: string;
  }

  // Then add 'key' to all our MenuItemDbs and save them as the new object type.
  const bankWithKeys = props.itemBank.map((item) => {
    return { ...item, key: item.id } as MenuItemDbWithKey;
  });

  const selection = React.useMemo(
    () =>
      new Selection({
        // Every time the Selection changes, set selectedItem to match.
        onSelectionChanged: (): void => {
          if (selection.getSelectedCount() === 1) {
            dispatch(setSelectedItem(selection.getSelection()[0]));
          } else {
            dispatch(setSelectedItem(undefined));
          }
        },
        items: bankWithKeys,
      }),
    [props.itemBank],
  );

  // If selectedItem is cleared, clear the Selection object to match.
  useEffect(() => {
    selectedItem === undefined && selection.setAllSelected(false);
  }, [selectedItem]);

  // Definitions of columns, including custom rendering
  const columns: CoherenceDataGridColumn<IMenuItemDb>[] = [
    {
      key: "name",
      name: "Item Name",
      type: "string",
      fieldName: "name",
      minColumnWidth: 100,
      isResizable: true,
      isRowHeader: true,
      //specifies the sorting to be done based on the menu item name
      getValue: (item) => item.name,
      //onRender: pass the new column component (item.name, item.description)
      onRender: (item: IMenuItemDb) => {
        return (
          <Stack>
            <StackItem>{item.name}</StackItem>
            <StackItem className={styles.itemGridCategory}>{item.description}</StackItem>
          </Stack>
        );
      },
    },
    {
      key: "category",
      name: "Category",
      fieldName: "category",
      type: "string",
      minColumnWidth: 100,
      isResizable: true,
    },
    {
      key: "price",
      name: "Price",
      fieldName: "amount",
      type: "currency",
      isResizable: true,
      minColumnWidth: 90,
      onRender: (item: IMenuItemDb) => {
        return <Text>{currentCurrency.currency + item.amount}</Text>;
      },
    },
    {
      key: "editDelete",
      name: "Options",
      type: "custom",
      isIconOnly: true,
      isResizable: true,
      minColumnWidth: 100,
      //rendering the edit and delete icons in this custom column
      onRender: (item: IMenuItemDb) => {
        return <EditDeleteColumn selectedGridDataGUID={item.id} />;
      },
    },
  ];

  return (
    <StackItem className={styles.itemGridSpacing}>
      <CoherenceDataGrid
        listConfig={columns}
        data={props.itemBank}
        // allow the user to choose the sort on this DataGrid
        isSortable={true}
        isScrollable={true}
        selection={selection}
        selectionMode={SelectionMode.single}
        checkButtonAriaLabel={"select row"}
        selectionPreservedOnEmptyClick={false}
        //hides the checkbox icon until element selection occurs
        checkboxVisibility={CheckboxVisibility.hidden}
        useFastIcons={false}
      />
    </StackItem>
  );
};
